import React from 'react';
import { formatDisplayDate } from '@/config';
import { Button, Input, Modal, Space, Table, message } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { PassengerInvalidPhoneModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
import PhoneCodeInput from '@/components/FormItems/PhoneCodeInput';

type Props = {
  canCorrect: boolean;
  onClose: () => void;
  onCorrected: () => void;
};

type Draft = { phoneCode?: string; phoneNumber?: string };

/**
 * Fiches de la compagnie à numéro invalide (introuvables par la recherche au guichet) : correction directe du numéro,
 * normalisé en E.164 par le serveur et tracé dans l'historique de la fiche.
 */
export default function InvalidPhonesModal({ canCorrect, onClose, onCorrected }: Props) {
  const { t } = useTranslation();
  const [rows, setRows] = React.useState<PassengerInvalidPhoneModel[]>([]);
  const [drafts, setDrafts] = React.useState<Record<string, Draft>>({});
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState<string>();

  React.useEffect(() => {
    apiSdk.BookingPassengerService.invalidPhones()
      .then(setRows)
      .catch(message.error)
      .finally(() => setLoading(false));
  }, []);

  const draftOf = (row: PassengerInvalidPhoneModel): Draft => drafts[row.id || ''] ?? { phoneCode: row.phoneCode || undefined };
  // Indicatif pré-rempli (celui de la fiche) gardé quand seul le numéro est saisi.
  const edit = (row: PassengerInvalidPhoneModel, change: Draft) => setDrafts(d => ({ ...d, [row.id || '']: { ...draftOf(row), ...change } }));

  const correct = (row: PassengerInvalidPhoneModel) => {
    const draft = draftOf(row);
    setSaving(row.id);
    apiSdk.BookingPassengerService.correctPhone({ body: { passengerId: row.id, phoneCode: draft.phoneCode, phoneNumber: draft.phoneNumber } })
      .then(res => {
        if (res?.success) {
          message.success(t('invalidPhones_Corrected'));
          setRows(r => r.filter(x => x.id !== row.id));
          onCorrected();
        } else if (res?.message) {
          message.error(t(res.message));
        }
      })
      .catch(message.error)
      .finally(() => setSaving(undefined));
  };

  return (
    <Modal open={true} width={1000} title={t('invalidPhones_Title')} footer={null} onCancel={onClose}>
      <p>{t('invalidPhones_Description')}</p>
      <Table<PassengerInvalidPhoneModel>
        data-testid="invalid-phones"
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        size="small"
        locale={{ emptyText: t('invalidPhones_Empty') }}
        columns={[
          { title: t('invalidPhones_Passenger'), render: (_, r) => `${r.firstName ?? ''} ${r.lastName ?? ''}`.trim() },
          { title: t('invalidPhones_Current'), render: (_, r) => `${r.phoneCode ?? ''} ${r.phoneNumber ?? ''}`.trim() },
          { title: t('invalidPhones_LastTravel'), dataIndex: 'lastTravelDate', render: v => formatDisplayDate(v) },
          ...(canCorrect
            ? [
                {
                  title: t('invalidPhones_Correction'),
                  render: (_: unknown, r: PassengerInvalidPhoneModel) => (
                    <Space.Compact>
                      <PhoneCodeInput
                        style={{ width: 110 }}
                        allowClear={false}
                        value={draftOf(r).phoneCode}
                        onChange={v => edit(r, { phoneCode: v })}
                        data-testid={`invalid-phone-code-${r.id}`}
                      />
                      <Input
                        style={{ width: 160 }}
                        value={draftOf(r).phoneNumber}
                        placeholder={t('invalidPhones_NewNumber')}
                        onChange={e => edit(r, { phoneNumber: e.target.value })}
                        onPressEnter={() => correct(r)}
                        data-testid={`invalid-phone-number-${r.id}`}
                      />
                      <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        loading={saving === r.id}
                        disabled={!draftOf(r).phoneNumber?.trim()}
                        onClick={() => correct(r)}
                        data-testid={`invalid-phone-save-${r.id}`}
                      />
                    </Space.Compact>
                  ),
                },
              ]
            : []),
        ]}
      />
    </Modal>
  );
}
